// Карточка изображения. SMY-74, переселена в ленту в SMY-114
//
// Ссылки на файлы спрашиваются по требованию: подпись у них со сроком, и
// хранить их в нагрузке артефакта значило бы отдавать протухшую ссылку тому,
// кто открыл результат из истории через неделю.
//
// Карточка намеренно пустая вокруг картинки. Из неё убраны запрос (он стоит
// пузырём прямо над ней), подпись «вариант 1» (вариант один, выбирать не из
// чего), строка списания (она над карточкой) и строка про окно бесплатной
// правки (она под полем ввода, где эту правку и набирают). Осталось само
// изображение, быстрые правки и «Скачать».

import { useEffect, useMemo, useState } from 'react'
import type { ImageVariants } from '@smyslika/core'

import { useSession } from '../session.tsx'
import { Card, Icon } from '../ui/index.ts'
import { CardHead, openFile, type CardProps } from './common.tsx'

/**
 * Правка изображения приходит из поля ввода, а не с чипов.
 *
 * Чипы («Фон светлее», «Убрать тень», «Ближе кадр») убраны 06.09.2026: они
 * обещали быструю правку, а на деле каждая была новой генерацией за 2,84 ₽ —
 * поставщик редактировать присланную картинку не умеет. Три кнопки рядом с
 * результатом читались как «нажимай сколько хочешь», и это было неправдой.
 * Правка осталась одна и словами, а сколько её и как долго — говорит строка
 * под полем ввода.
 */
export type ImageCardProps = CardProps

export function ImageCard({ artifact, runId }: ImageCardProps) {
  const { files } = useSession()
  const [urls, setUrls] = useState<(string | null)[]>([])
  const [zoom, setZoom] = useState<string | null>(null)

  const card = artifact.payload as ImageVariants
  // Список сам по себе — новый массив на каждую отрисовку, и эффект по нему
  // ходил бы за ссылками бесконечно.
  const ids = useMemo(() => card.fileIds ?? [], [card.fileIds])

  useEffect(() => {
    let alive = true
    void Promise.all(ids.map((id) => files.url(id))).then((got) => {
      if (alive) setUrls(got)
    })
    return () => {
      alive = false
    }
  }, [files, ids])

  const url = urls[0] ?? null

  return (
    <Card className="rcard image">
      {/* Запроса в шапке нет: он стоит ровно над карточкой пузырём человека,
          и повторять его значит показывать одно и то же дважды подряд.
          Пометка закона ушла под значок «i» — читают её один раз, а места
          она занимала как абзац ответа. */}
      <CardHead title="Изображение" {...(runId != null ? { runId } : {})} info={card.notice} />

      <div className="image__variants">
        {ids.map((id, i) => (
          // Вариант один (`scenarioScope.image` = 1), и подписи «вариант 1»
          // больше нет: она называла номер там, где выбирать не из чего.
          // Цикл остался — вернётся второй вариант, вернутся и подписи.
          <button
            key={id}
            type="button"
            className="image__variant"
            aria-label="Открыть изображение крупнее"
            disabled={urls[i] == null}
            onClick={() => setZoom(urls[i] ?? null)}
          >
            {urls[i] != null ? (
              <img src={urls[i] as string} alt={card.prompt} />
            ) : (
              // Полоски — не украшение из макета, а честное «файл ещё едет».
              // Ссылка подписывается запросом, и ждать её квадрат обязан
              // чем-то, а не пустотой.
              <span className="image__pending" />
            )}
          </button>
        ))}
        {ids.length === 0 ? (
          // Файлов нет — значит хранилище не приняло их при завершении
          // запуска. Результат при этом настоящий, и молчать об этом нельзя:
          // человек ждёт картинку, а не пустой квадрат.
          <p className="muted image__lost">
            Картинки не сохранились — их не удалось положить в хранилище. Списания за это не было; попробуй
            повторить задачу.
          </p>
        ) : null}
      </div>


      <div className="image__actions">
        <button
          type="button"
          className="btn btn--ghost btn--small"
          disabled={url == null}
          onClick={() => {
            if (url != null) openFile(url)
          }}
        >
          Скачать
        </button>
      </div>

      {/* Ни строки про списание, ни строки про окно правки: первая стоит над
          карточкой (`RunLine`), вторая — под полем ввода, где человек эту
          правку и наберёт. */}
      {zoom != null ? <Lightbox url={zoom} alt={card.prompt} onClose={() => setZoom(null)} /> : null}
    </Card>
  )
}

/**
 * Изображение крупно, поверх затемнения.
 *
 * Своё окно, а не `Sheet`: тот рисует белую карточку с полями под текст, а
 * здесь содержимое — сама картинка, и рамка вокруг неё только мешает.
 * Закрывается по Esc, крестику и клику по фону — все три способа человек
 * пробует не задумываясь.
 */
function Lightbox({ url, alt, onClose }: { url: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    // Фон не должен прокручиваться под окном: иначе колесо мыши уводит ленту,
    // а картинка остаётся на месте.
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
    }
  }, [onClose])

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Изображение крупнее"
      onClick={(e) => {
        // Только по самому фону: клик по картинке закрывать окно не должен.
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <button type="button" className="lightbox__close" aria-label="Закрыть" onClick={onClose}>
        <Icon name="close" size={22} strokeWidth={2} />
      </button>
      <img className="lightbox__image" src={url} alt={alt} />
    </div>
  )
}
